import { useState, useCallback, useMemo } from 'react';
import { countries, Country } from '@/data';
import type { Language } from '@/config/locales';

// 국가별 전화번호 최대 길이
const maxLengths: { [key: string]: number } = {
  '+1': 10,    // USA, Canada
  '+7': 10,    // Russia
  '+20': 10,   // Egypt
  '+27': 9,    // South Africa
  '+30': 10,   // Greece
  '+31': 9,    // Netherlands
  '+32': 9,    // Belgium
  '+33': 9,    // France
  '+34': 9,    // Spain
  '+36': 9,    // Hungary
  '+39': 10,   // Italy
  '+40': 9,    // Romania
  '+41': 9,    // Switzerland
  '+43': 10,   // Austria
  '+44': 10,   // UK
  '+45': 8,    // Denmark
  '+46': 9,    // Sweden
  '+47': 8,    // Norway
  '+48': 9,    // Poland
  '+49': 11,   // Germany
  '+51': 9,    // Peru
  '+52': 10,   // Mexico
  '+53': 8,    // Cuba
  '+54': 10,   // Argentina
  '+55': 11,   // Brazil
  '+56': 9,    // Chile
  '+57': 10,   // Colombia
  '+58': 10,   // Venezuela
  '+60': 9,    // Malaysia
  '+61': 9,    // Australia
  '+62': 11,   // Indonesia
  '+63': 10,   // Philippines
  '+64': 9,    // New Zealand
  '+65': 8,    // Singapore
  '+66': 9,    // Thailand
  '+81': 10,   // Japan
  '+82': 11,   // South Korea
  '+84': 10,   // Vietnam
  '+86': 11,   // China
  '+90': 10,   // Turkey
  '+91': 10,   // India
  '+92': 10,   // Pakistan
  '+93': 9,    // Afghanistan
  '+94': 9,    // Sri Lanka
  '+95': 9,    // Myanmar
  '+98': 10,   // Iran
  '+212': 9,   // Morocco
  '+213': 9,   // Algeria
  '+216': 8,   // Tunisia
  '+218': 9,   // Libya
  '+220': 7,   // Gambia
  '+221': 9,   // Senegal
  '+223': 8,   // Mali
  '+224': 9,   // Guinea
  '+225': 10,  // Ivory Coast
  '+234': 10,  // Nigeria
  '+241': 7,   // Gabon
  '+244': 9,   // Angola
  '+254': 10,  // Kenya
  '+255': 9,   // Tanzania
  '+256': 9,   // Uganda
  '+351': 9,   // Portugal
  '+352': 9,   // Luxembourg
  '+353': 9,   // Ireland
  '+354': 7,   // Iceland
  '+358': 10,  // Finland
  '+420': 9,   // Czech Republic
  '+501': 7,   // Belize
  '+502': 8,   // Guatemala
  '+503': 8,   // El Salvador
  '+504': 8,   // Honduras
  '+505': 8,   // Nicaragua
  '+506': 8,   // Costa Rica
  '+507': 8,   // Panama
  '+591': 8,   // Bolivia
  '+593': 9,   // Ecuador
  '+595': 9,   // Paraguay
  '+598': 8,   // Uruguay
  '+852': 8,   // Hong Kong
  '+853': 8,   // Macao
  '+886': 9,   // Taiwan
  '+971': 9,   // UAE
  '+972': 9,   // Israel
  '+973': 8,   // Bahrain
  '+974': 8,   // Qatar
  '+966': 9,   // Saudi Arabia
};

export interface UseLoginFormReturn {
  // 폼 상태
  email: string;
  setEmail: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  showPassword: boolean;
  setShowPassword: (value: boolean) => void;
  phoneNumber: string;
  selectedCountry: Country;
  showCountryDropdown: boolean;
  setShowCountryDropdown: (value: boolean) => void;
  countrySearch: string;
  setCountrySearch: (value: string) => void;

  // 동의 상태
  agreeWhatsApp: boolean;
  setAgreeWhatsApp: (value: boolean) => void;
  agreeTerms: boolean;
  setAgreeTerms: (value: boolean) => void;
  agreePrivacy: boolean;
  setAgreePrivacy: (value: boolean) => void;
  /**
   * LFPDPPP Primary 동의 (필수): 서비스 운영 / 결제 처리 / ARCO 권리.
   * 미체크 시 회원가입 차단. (#372 / admin#1361)
   */
  agreePrimary: boolean;
  setAgreePrimary: (value: boolean) => void;
  /**
   * LFPDPPP Secondary 동의 (선택): 마케팅 / 통계.
   * 옵트인. 회원가입 후 BE 에 별도 POST. (#372 / admin#1361)
   */
  agreeSecondary: boolean;
  setAgreeSecondary: (value: boolean) => void;

  // 모달 상태
  showTermsModal: boolean;
  setShowTermsModal: (value: boolean) => void;
  showPrivacyModal: boolean;
  setShowPrivacyModal: (value: boolean) => void;
  showTermsOfService: boolean;
  setShowTermsOfService: (value: boolean) => void;
  showPrivacyPolicy: boolean;
  setShowPrivacyPolicy: (value: boolean) => void;
  showVerificationPage: boolean;
  setShowVerificationPage: (value: boolean) => void;

  // 핸들러
  handleAgreeAll: (checked: boolean) => void;
  handlePhoneNumberChange: (value: string) => void;
  handleSelectCountry: (country: Country) => void;
  handleCloseCountryDropdown: () => void;

  // 계산된 값
  isAllAgreed: boolean;
  isPhoneNumberValid: boolean;
  isButtonEnabled: boolean;
  filteredCountries: Country[];

  // 유틸리티
  getCountryName: (country: Country) => string;
  getFullPhoneNumber: () => string;
}

export function useLoginForm(language: Language): UseLoginFormReturn {
  // 기본 폼 상태
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<Country>(
    countries.find(c => c.code === '+52') || countries[0]
  );
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');

  // 동의 상태
  const [agreeWhatsApp, setAgreeWhatsApp] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  // LFPDPPP 동의 (#372): Primary 필수 / Secondary 옵트인
  const [agreePrimary, setAgreePrimary] = useState(false);
  const [agreeSecondary, setAgreeSecondary] = useState(false);

  // 모달 상태
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showVerificationPage, setShowVerificationPage] = useState(false);

  // 핸들러
  const handleAgreeAll = useCallback((checked: boolean) => {
    setAgreeWhatsApp(checked);
    setAgreeTerms(checked);
    setAgreePrivacy(checked);
    setAgreePrimary(checked);
    setAgreeSecondary(checked);
  }, []);

  const handlePhoneNumberChange = useCallback((inputValue: string) => {
    // 숫자만 허용
    const value = inputValue.replace(/\D/g, '');

    // 선택된 국가의 최대 길이
    const maxLength = maxLengths[selectedCountry.code] || 15;

    // 최대 길이 내에서만 업데이트
    if (value.length <= maxLength) {
      setPhoneNumber(value);
    }
  }, [selectedCountry.code]);

  const handleSelectCountry = useCallback((country: Country) => {
    setSelectedCountry(country);
    setShowCountryDropdown(false);
    setCountrySearch('');
  }, []);

  const handleCloseCountryDropdown = useCallback(() => {
    setShowCountryDropdown(false);
    setCountrySearch('');
  }, []);

  // 국가명 가져오기
  const getCountryName = useCallback((country: Country) => {
    switch (language) {
      case 'ko': return country.nameKo;
      case 'es': return country.nameEs;
      case 'en': return country.nameEn;
      default: return country.nameEn;
    }
  }, [language]);

  // 계산된 값
  // LFPDPPP Primary 동의 = Terms + Privacy 통합 (관계자 동의 정책 정합).
  // 기존 agreeTerms/agreePrivacy 토글이 모두 체크되면 Primary 도 자동 체크되도록
  // 보조 효과는 LoginPage 핸들러 측에서 sync 처리 (이 훅은 raw state 관리만).
  const isAllAgreed = agreeWhatsApp && agreeTerms && agreePrivacy;

  // 전화번호 유효성 검사
  const isPhoneNumberValid = useMemo(() => {
    if (!phoneNumber) return false;

    const length = phoneNumber.length;

    switch (selectedCountry.code) {
      case '+82': // South Korea
        return length >= 10 && length <= 11;
      case '+1': // US, Canada
        return length === 10;
      case '+52': // Mexico
        return length === 10;
      case '+81': // Japan
        return length >= 10 && length <= 11;
      case '+86': // China
        return length === 11;
      case '+44': // UK
        return length === 10;
      case '+33': // France
        return length === 9;
      case '+49': // Germany
        return length >= 10 && length <= 11;
      case '+39': // Italy
        return length === 10;
      case '+34': // Spain
        return length === 9;
      case '+61': // Australia
        return length === 9;
      case '+91': // India
        return length === 10;
      case '+55': // Brazil
        return length === 11;
      default:
        // 일반 유효성: 7-15자리
        return length >= 7 && length <= 15;
    }
  }, [phoneNumber, selectedCountry.code]);

  // 버튼 활성화: 전화번호 유효 + 기존 legacy 동의 (Terms+Privacy+WhatsApp) 모두 + LFPDPPP Primary.
  // LFPDPPP 명시적/구체적 동의 원칙 — Primary 권위 강화 (#372 양 채널 1R P0-1 시정).
  // handleAgreeAll 이 모든 토글을 sync 하므로 "전체 동의" UX 경로는 회귀 없음.
  const isButtonEnabled = isPhoneNumberValid && isAllAgreed && agreePrimary;

  // 필터된 국가 목록 (검색어가 없으면 전체 반환)
  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) {
      return countries;
    }
    const searchLower = countrySearch.toLowerCase();
    return countries.filter(country =>
      getCountryName(country).toLowerCase().includes(searchLower) ||
      country.code.includes(searchLower) ||
      country.country.toLowerCase().includes(searchLower)
    );
  }, [countrySearch, getCountryName]);

  // 전체 전화번호
  const getFullPhoneNumber = useCallback(() => {
    return `${selectedCountry.code} ${phoneNumber}`;
  }, [selectedCountry.code, phoneNumber]);

  return {
    // 폼 상태
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    phoneNumber,
    selectedCountry,
    showCountryDropdown,
    setShowCountryDropdown,
    countrySearch,
    setCountrySearch,

    // 동의 상태
    agreeWhatsApp,
    setAgreeWhatsApp,
    agreeTerms,
    setAgreeTerms,
    agreePrivacy,
    setAgreePrivacy,
    agreePrimary,
    setAgreePrimary,
    agreeSecondary,
    setAgreeSecondary,

    // 모달 상태
    showTermsModal,
    setShowTermsModal,
    showPrivacyModal,
    setShowPrivacyModal,
    showTermsOfService,
    setShowTermsOfService,
    showPrivacyPolicy,
    setShowPrivacyPolicy,
    showVerificationPage,
    setShowVerificationPage,

    // 핸들러
    handleAgreeAll,
    handlePhoneNumberChange,
    handleSelectCountry,
    handleCloseCountryDropdown,

    // 계산된 값
    isAllAgreed,
    isPhoneNumberValid,
    isButtonEnabled,
    filteredCountries,

    // 유틸리티
    getCountryName,
    getFullPhoneNumber,
  };
}
